import { type FC, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import {
  AgentBlob,
  type ChatConversation,
  type ChatHistoryOverviewItem,
  type ChatMessage,
  Flex,
  ResponseFeedback,
  Reveal,
  Text,
  nextFrame,
  useMaybePubSub,
  useOnce,
} from "@huub-web/ui";
import { useAuth } from "@huub-web/backend";
import { debounce, isObject, throttle } from "@huub-web/utils";
import {
  TemplatesParser,
  PageContentProviderActive,
} from "@huub-web/templating";

import {
  SideAgentContent,
  type SideAgentContentProps,
  type SideAgentSlide,
  SIDE_CONTENT_SCROLL_EMIT_TAG,
} from "../SideAgentContent";
import { useNavigationState } from "../../providers";
import {
  useAppController,
  useAppSettings,
  useCompactLayout,
  useNavigate,
  useTaskBar,
} from "../../hooks";
import { StreamingAgentResponseController } from "../StreamingAgentResponseController/StreamingAgentResponseController";
import { TaskBarController } from "../TaskBarController/TaskBarController";
import type { ChatMessageData } from "../../contexts/AppControllerContext";
import { responsesMotionProps } from "../StreamingAgentResponseController/StreamingAgentResponseController.motion";

import { templateWrapperStyle } from "./SideAgent.css";

const GIT_HASH = import.meta.env.VITE_GIT_HASH;

type SideAgentProps = Omit<
  SideAgentContentProps<ChatMessageData>,
  "barTrailing" | "trailing"
> & {
  onOpenChange?: (open: boolean) => void;
};

/**
 * Basic side agent component that provides a collapsible side panel
 */
export const SideAgent: FC<SideAgentProps> = ({ onOpenChange, ...props }) => {
  const { state, conversation, isMessageStreaming, isWaitingForAgentResponse } =
    useAppController();
  const { isCompact } = useCompactLayout();
  const pubSub = useMaybePubSub();
  const { navigate } = useNavigate();
  const { isVisualDebugEnabled, theme, isResponseFeedbackEnabled } =
    useAppSettings();
  const { indentity } = useAuth();
  const {
    isSideContentOpen,
    setSideContentOpen,
    sideContentVariant,
    navigationContext,
  } = useNavigationState();
  const {
    isLoading: isTaskBarLoading,
    isFeedbackLoading,
    hasFeedbackBeenSend,
    handleFeedbackSubmit,
  } = useTaskBar();

  useEffect(() => {
    onOpenChange?.(isSideContentOpen);
  }, [isSideContentOpen]);

  useEffect(() => {
    const unsubscribe = pubSub?.subscribe("sideContent", ({ action }) => {
      setSideContentOpen(action === "open");
    });
    return () => unsubscribe?.();
  }, []);

  const scrollToBottom = () => {
    nextFrame(() => {
      pubSub?.publish("scrollToBottom", {
        tag: SIDE_CONTENT_SCROLL_EMIT_TAG,
      });
    });
  };

  const scrollToBottomInstant = () => {
    nextFrame(() => {
      pubSub?.publish("scrollToBottom", {
        tag: SIDE_CONTENT_SCROLL_EMIT_TAG,
        instant: true,
      });
    });
  };

  const scrollToBottomThrottled = useRef(throttle(scrollToBottom, 250));
  const scrollToBottomInstantDebounced = useRef(
    debounce(scrollToBottomInstant, 500),
  );

  const handleScrollToBottom = (instant?: boolean) => {
    if (instant) {
      scrollToBottomInstantDebounced.current();
    } else {
      scrollToBottomThrottled.current();
    }
  };

  useEffect(() => {
    handleScrollToBottom();
  }, [conversation]);

  useOnce(() => {
    handleScrollToBottom(true);
  });

  useEffect(() => {
    if (!isCompact) {
      setSideContentOpen(false);
    }
  }, [isCompact]);

  const parsedConversation: ChatConversation<ChatMessageData> | null = (() => {
    if (!conversation) {
      return null;
    }

    // Create the Set outside the filter to track duplicates across all entries
    const seenIDs = new Set<string>();

    const entries =
      conversation?.entries
        ?.map((entry) => {
          const author = isObject(entry.author)
            ? {
                name: indentity?.name,
                email: indentity?.email,
                type: entry.author.type ?? "human",
              }
            : entry.author;

          return {
            ...entry,
            author,
          };
        })
        // Filter out entries that are not in the current navigation context
        ?.filter((entry) => {
          // If no navigation context is active, include all entries
          if (!navigationContext) {
            return true;
          }
          // If navigation context is active, only include entries that match it
          // Exclude entries without a context when navigation context is active
          if (!entry.context) {
            return false;
          }
          return navigationContext.join(",") === entry.context.join(",");
        })
        // Filter out duplicate entries by ID because the BE returns duplicate entries -_-
        ?.filter((entry) => {
          if (!entry.id) {
            return true;
          } // Keep if no ID
          if (seenIDs.has(entry.id)) {
            return false;
          }
          seenIDs.add(entry.id);

          return true;
        }) ?? [];

    const parsedConversation = {
      ...conversation,
      entries,
    };

    return parsedConversation;
  })();

  // Slides
  // -------------------

  const [slide, setSlide] = useState<SideAgentSlide>("agent");

  const handleSlideChange = (slide: SideAgentSlide) => {
    setSlide(slide);
  };

  useEffect(() => {
    // If chathistory is open and we navigate away, move back to agent slide
    if (navigationContext?.[0] !== "chatHistory") {
      setSlide("agent");
    }
  }, [navigationContext]);

  // Handlers
  // --------

  const handleHistoryChatClick = (item: ChatHistoryOverviewItem) => {
    if (!item.thread_id) {
      console.warn("No 'thread_id' found for chat history item", item);
      return;
    }
    navigate(`/gesprekshistorie/${item.thread_id}`);
  };

  const handleHistoryClick = () => {
    setSlide("history");
  };

  const handleMapAIResponseTrailing = (
    message: ChatMessage<ChatMessageData>,
  ) => {
    return (
      <>
        {message?.data?.templates && (
          <PageContentProviderActive.Provider value={true}>
            <motion.div
              {...responsesMotionProps.sideContent}
              className={templateWrapperStyle}
            >
              <Flex direction="column" gap="xxs" overflowHidden>
                <TemplatesParser templates={message.data.templates} level={1} />
              </Flex>
            </motion.div>
          </PageContentProviderActive.Provider>
        )}
        {isResponseFeedbackEnabled && (
          <Reveal>
            {message?.data?.feedback && (
              <ResponseFeedback
                key={message.data.feedback.message_id}
                loading={isFeedbackLoading}
                comments={message.data.feedback.feedback_options?.comments}
                tags={message.data.feedback.feedback_options?.tags}
                onSubmit={handleFeedbackSubmit}
                disabled={hasFeedbackBeenSend}
                tagOptions={
                  message.data.feedback.feedback_options?.available_tags
                }
                modalTrailing={
                  (__APP_VERSION__ || GIT_HASH) && (
                    <Text variant="bodyXS" color="currentColor50">
                      {__APP_VERSION__} {(GIT_HASH ?? "local").slice(0, 7)}
                    </Text>
                  )
                }
              />
            )}
          </Reveal>
        )}
      </>
    );
  };

  const handleBackButtonClick = () => {
    if (navigationContext?.[0] === "chatHistory") {
      navigate("/");
    }
  };

  return (
    <SideAgentContent
      theme={theme}
      debug={isVisualDebugEnabled}
      onSlideChange={handleSlideChange}
      onHistoryChatClick={handleHistoryChatClick}
      onBackButtonClick={handleBackButtonClick}
      mapAIResponseTrailing={handleMapAIResponseTrailing}
      conversation={parsedConversation ?? undefined}
      agentIsLoading={isTaskBarLoading}
      slide={navigationContext?.[0] === "chatHistory" ? "history" : slide}
      state={state}
      trailing={
        isMessageStreaming ? (
          <StreamingAgentResponseController
            variant="sideContent"
            showTemplates={false}
            showFeedback={false}
            showTime={false}
            onChange={handleScrollToBottom}
          />
        ) : isWaitingForAgentResponse ? (
          <AgentBlob />
        ) : null
      }
      lastTrailing={
        sideContentVariant === "default" && (
          <TaskBarController
            variant="sideContent"
            history
            onHistoryClick={handleHistoryClick}
            disabled={
              slide === "history" || navigationContext?.[0] === "chatHistory"
            }
          />
        )
      }
      {...props}
    />
  );
};
